import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT } from '../cardMesh';

// Summoning sickness. Drowsy "z" glyphs drifting up off the card.
//
// A minion that entered the realm this turn cannot move or attack, and
// nothing on the card said so: players read a fresh minion as ready and
// then wondered why the drag halo never lit. Three glyph sprites rise from
// the card's top edge on a slow loop, each fading out as it climbs, so the
// card reads as asleep at a glance without hiding the art the way a
// full-card fog would.
//
// Sprites (camera-facing) rather than the lay-flat shader layers the
// keyword effects use: the glyph has to stay legible from every camera
// angle, and it lives ABOVE the card, so stack occlusion is not a concern.

const GLYPH_COUNT = 3;
const CYCLE_S = 2.6;         // one glyph's full rise + fade
const RISE_HEIGHT = 2.4;     // world units climbed over a cycle
const DRIFT_X = 0.9;         // sideways wobble amplitude
const BASE_LIFT = 0.35;      // start height above the card face
const SPRITE_SIZE = 1.35;
const CANVAS = 128;

let cachedTexture = null;
function glyphTexture() {
  if (cachedTexture) return cachedTexture;
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS;
  canvas.height = CANVAS;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, CANVAS, CANVAS);
  ctx.font = `bold ${Math.round(CANVAS * 0.72)}px Georgia, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = CANVAS * 0.09;
  ctx.strokeStyle = 'rgba(10, 8, 20, 0.85)';
  ctx.strokeText('z', CANVAS / 2, CANVAS / 2 + CANVAS * 0.04);
  ctx.fillStyle = '#d9d2f5';
  ctx.fillText('z', CANVAS / 2, CANVAS / 2 + CANVAS * 0.04);
  cachedTexture = new THREE.CanvasTexture(canvas);
  cachedTexture.colorSpace = THREE.SRGBColorSpace;
  return cachedTexture;
}

export function createSummoningSleepEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;
  const sprites = [];
  for (let i = 0; i < GLYPH_COUNT; i += 1) {
    const material = new THREE.SpriteMaterial({
      map: glyphTexture(),
      transparent: true,
      depthWrite: false,
      opacity: 0,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.setScalar(SPRITE_SIZE);
    sprite.frustumCulled = false;
    group.add(sprite);
    sprites.push(sprite);
  }
  return {
    points: group,
    sprites,
    // Stagger the glyphs a third of a cycle apart and randomise the start
    // so neighbouring sleepy minions don't pulse in lockstep.
    time: Math.random() * CYCLE_S,
  };
}

export function updateSummoningSleepEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;
  effect.time += dt;
  const { points: group, sprites } = effect;
  group.position.copy(cardMesh.position);
  group.quaternion.copy(cardMesh.quaternion);
  // Anchor at the card's upper-right corner (card-local), lifted off the
  // face. The group inherits the card's yaw so "upper-right" follows the
  // controller's orientation.
  const ax = CARD_WIDTH * 0.32;
  const ay = CARD_HEIGHT * 0.36;
  for (let i = 0; i < sprites.length; i += 1) {
    const phase = ((effect.time + (i * CYCLE_S) / sprites.length) % CYCLE_S) / CYCLE_S;
    const rise = phase * RISE_HEIGHT;
    const wobble = Math.sin(phase * Math.PI * 2 + i) * DRIFT_X * phase;
    sprites[i].position.set(ax + wobble, ay - phase * 0.4, BASE_LIFT + rise);
    // Fade in over the first 15%, hold, fade out over the last 45%.
    const alpha = phase < 0.15 ? phase / 0.15 : phase > 0.55 ? 1 - (phase - 0.55) / 0.45 : 1;
    sprites[i].material.opacity = Math.max(0, Math.min(1, alpha)) * 0.9;
    const grow = 0.75 + phase * 0.5;
    sprites[i].scale.setScalar(SPRITE_SIZE * grow);
  }
}

export function disposeSummoningSleepEffect(effect) {
  if (!effect) return;
  const { points: group, sprites } = effect;
  group.parent?.remove(group);
  for (const s of sprites || []) s.material.dispose();
}
